'use client'

import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { cn } from '@kiao-voice/ui-kit'
import { TITLE_COMPONENTS } from './title-markup'
import type { SiteSectionTone } from './section'

/**
 * 區塊標頭：eyebrow → h2 →（選填）hook 一句 →（選填）lead。
 *
 * 不限寬。設計系統原本給 `.sec-head` 46em、`.lead` 38em，在 1140px 的容器裡會
 * 比容器早一截就換行、右側空一大塊；一律填滿容器寬度再折行。
 *
 * `titleKey`／`hookKey` 收 i18n key 而不是字串，理由同 {@link InkHero}：這兩處
 * 是襯線 subset 字型，字集掃描器只看得到原始碼裡的字面 key。
 */

export interface SectionHeadProps {
  eyebrow?: string
  titleKey: string
  /** 粗體一句，接在標題下面。display 字體。 */
  hookKey?: string
  lead?: React.ReactNode
  /** 墨底區塊要反白。 */
  tone?: SiteSectionTone
  /** 標題層級。區塊內的次級標題（例如示範卡上方那行）用 3。 */
  level?: 2 | 3
  /** `block` 是區塊內次級標題的字級，不是整個 section 的門面。 */
  size?: 'section' | 'block'
  align?: 'start' | 'center'
  titleLeading?: 'default' | 'roomy'
  className?: string
}

export function SectionHead({
  eyebrow,
  titleKey,
  hookKey,
  lead,
  tone = 'paper',
  level = 2,
  size = 'section',
  align = 'start',
  titleLeading = 'default',
  className,
}: SectionHeadProps) {
  const Heading = level === 2 ? 'h2' : 'h3'
  const { t } = useTranslation(['web'])
  const ink = tone === 'ink'
  return (
    <div className={cn(align === 'center' && 'text-center', className)}>
      {eyebrow ? (
        <p
          className={cn(
            'mb-(--gap-md) font-mono text-xs tracking-[0.28em] uppercase',
            ink ? 'text-background/62' : 'text-muted'
          )}
        >
          {eyebrow}
        </p>
      ) : null}
      {/* data-display-font 是襯線 subset 的涵蓋範圍標記：字型覆蓋測試靠它把
          「畫面上真的用襯線畫出來的字」全部撈出來比對，不必猜哪些選擇器算數。 */}
      <Heading
        data-display-font=""
        className={cn(
          'font-display font-semibold tracking-[0.005em]',
          size === 'section'
            ? cn(
                'text-(length:--fs-marketing-h2)',
                titleLeading === 'roomy' ? 'leading-[1.32]' : 'leading-[1.22]'
              )
            : 'text-(length:--fs-marketing-h3) leading-[1.4]'
        )}
      >
        <Trans t={t} i18nKey={titleKey} components={TITLE_COMPONENTS} />
      </Heading>
      {hookKey ? (
        <p
          data-display-font=""
          className="mt-(--gap-md) font-display text-2xl leading-[1.4] font-semibold"
        >
          <Trans t={t} i18nKey={hookKey} components={TITLE_COMPONENTS} />
        </p>
      ) : null}
      {lead ? (
        <p
          className={cn(
            'mt-(--gap-md) text-[length:var(--fs-marketing-lead)] leading-[1.7]',
            ink ? 'text-background/74' : 'text-muted'
          )}
        >
          {lead}
        </p>
      ) : null}
    </div>
  )
}
